import React from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Play, Code2, Zap, Database } from 'lucide-react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'

export const HeroSection: React.FC = () => {
  const { t } = useTranslation(['homepage', 'common'])

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-kylix-background via-white to-kylix-lightgray">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-64 h-64 bg-primary-200/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary-200/20 rounded-full blur-3xl"></div>
      </div>
      
      <div className="container relative z-10 pt-20">
        <div className="max-w-6xl mx-auto text-center">
          {/* Main Headline */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6">
              <span className="text-gray-900">{t('homepage:hero.title')}</span>
              <br />
              <span className="bg-gradient-to-r from-kylix-primary to-kylix-secondary bg-clip-text text-transparent">{t('homepage:hero.subtitle')}</span>
            </h1>
          </motion.div>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-gray-600 mb-12 max-w-4xl mx-auto leading-relaxed"
          >
            {t('homepage:hero.description')}
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-16"
          >
            <Link to="/development-notice" className="btn-primary text-lg px-8 py-4">
              {t('homepage:hero.cta')}
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/development-notice" className="btn-secondary text-lg px-8 py-4">
              <Play className="w-5 h-5" />
              {t('homepage:hero.watchDemo')}
            </Link>
          </motion.div>

          {/* Tech Stack Indicators */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto"
          >
            <div className="flex items-center justify-center space-x-3 p-6 bg-white/70 backdrop-blur-sm rounded-2xl shadow-sm border border-white/50">
              <Code2 className="w-8 h-8 text-kylix-secondary" />
              <div className="text-left">
                <p className="font-semibold text-gray-900">form_definitions</p>
                <p className="text-sm text-gray-600">Dynamic UI Generation</p>
              </div>
            </div>
            
            <div className="flex items-center justify-center space-x-3 p-6 bg-white/70 backdrop-blur-sm rounded-2xl shadow-sm border border-white/50">
              <Zap className="w-8 h-8 text-kylix-secondary" />
              <div className="text-left">
                <p className="font-semibold text-gray-900">AI Integration</p>
                <p className="text-sm text-gray-600">OpenAI Native</p>
              </div>
            </div>
            
            <div className="flex items-center justify-center space-x-3 p-6 bg-white/70 backdrop-blur-sm rounded-2xl shadow-sm border border-white/50">
              <Database className="w-8 h-8 text-kylix-secondary" />
              <div className="text-left">
                <p className="font-semibold text-gray-900">Multi-Tenant SaaS</p>
                <p className="text-sm text-gray-600">Enterprise Ready</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <div className="flex flex-col items-center space-y-2">
          <span className="text-sm text-gray-500 font-medium">Descubra como</span>
          <div className="w-0.5 h-8 bg-gradient-to-b from-kylix-secondary to-transparent animate-pulse-slow"></div>
        </div>
      </motion.div>
    </section>
  )
}